import * as React from "react";
import "bulma/css/bulma.css";
import {Button, Control, Field, Icon, Input, Select, TextArea} from "bloomer";
import styled from "react-emotion";
import Spacer from "../../elements/Spacer";
import InputWarning from "../../elements/InputWarning";
import Countries from "./Countries";
import Subjects from "./Subjects";
import ErrorMessage from "../../elements/ErrorMessage";
import {sendUserFeedback} from "../../api/Contact";
import ErrorUtils from "../../utils/ErrorUtils";
import {toast} from "react-toastify";
import {EMAIL_REGEX, INVALID_FIELD_STYLE} from "../../utils/Constants";

const StyledLabel = styled("span")`
    text-align: left;
    font-size: 16px;
`;

const MandatoryField = styled("span")`
    color: red;
    font-size: 16px;
`;

const FieldSpacer = styled("div")`
    height: 6px;
`;

interface IContactUsFormProps {
    onClose: any;
}

interface IContactUsFormState {
    sending: boolean;
    sentSuccessfully: boolean;
    showMandatoryFields: boolean;
    firstName: string;
    firstNameInvalid: boolean;
    lastName: string;
    lastNameInvalid: boolean;
    email: string;
    emailInvalid: boolean;
    emailErrorMessage: string;
    country: string;
    countryInvalid: boolean;
    companyName: string;
    subject: string;
    subjectInvalid: boolean;
    additionalInfo: string;
    additionalInfoInvalid: boolean;
    showAdditionalInfoLimitWarning: boolean;
    errorMessage: string;
}

class ContactUsForm extends React.Component<IContactUsFormProps, IContactUsFormState> {
    public ADDITIONAL_INFO_MAX_LENGTH = 5000;

    public countries = Countries;
    public subjects = Subjects;

    public constructor(props: IContactUsFormProps) {
        super(props);

        this.state = {
            sending: false,
            sentSuccessfully: false,
            showMandatoryFields: false,
            firstName: "",
            firstNameInvalid: false,
            lastName: "",
            lastNameInvalid: false,
            email: "",
            emailInvalid: false,
            emailErrorMessage: "",
            country: "",
            countryInvalid: false,
            companyName: "",
            subject: "",
            subjectInvalid: false,
            additionalInfo: "",
            additionalInfoInvalid: false,
            showAdditionalInfoLimitWarning: false,
            errorMessage: ""
        };
    }

    public handleFirstNameChange = (e: any) => {
        const value = e.target.value;
        this.setState({
            firstName: value,
            firstNameInvalid: !value.trim()
        });
    };

    public handleLastNameChange = (e: any) => {
        const value = e.target.value;
        this.setState({
            lastName: value,
            lastNameInvalid: !value.trim()
        });
    };

    public handleEmailChange = (e: any) => {
        const value = e.target.value;
        const emailErrorMessage = this.validateEmail(value.trim());
        this.setState({
            email: value,
            emailInvalid: emailErrorMessage.length > 0,
            emailErrorMessage
        });
    };

    public handleCompanyChange = (e: any) => {
        this.setState({
            companyName: e.target.value
        });
    };

    public handleCountryChange = (e: any) => {
        const value = e.target.value;
        this.setState({
            country: value,
            countryInvalid: value === '-'
        });
    };

    public handleSubjectChange = (e: any) => {
        const value = e.target.value;
        this.setState({
            subject: value,
            subjectInvalid: value === '-'
        });
    };

    public handleAdditionalInfoChange = (e: any) => {
        const value = e.target.value;
        this.setState({
            additionalInfo: value,
            additionalInfoInvalid: !value.trim(),
            showAdditionalInfoLimitWarning: value.trim().length > this.ADDITIONAL_INFO_MAX_LENGTH
        });
    };

    public validateEmail = (value: any) => {
        if (!value) {
            return "Required field.";
        } else if (!EMAIL_REGEX.test(value)) {
            return "Please enter valid email.";
        }
        return "";
    };

    public closeForm = () => {
        this.props.onClose();
    };

    public submitForm = async () => {
        let valid = true;
        if (!this.state.firstName.trim()) {
            this.setState({
                firstNameInvalid: true
            });
            valid = false;
        }
        if (!this.state.lastName.trim()) {
            this.setState({
                lastNameInvalid: true
            });
            valid = false;
        }
        if (!this.state.email.trim()) {
            this.setState({
                emailInvalid: true,
                emailErrorMessage: 'Required field.'
            });
            valid = false;
        }
        if (!this.state.country.trim()) {
            this.setState({
                countryInvalid: true
            });
            valid = false;
        }
        if (!this.state.subject.trim()) {
            this.setState({
                subjectInvalid: true
            });
            valid = false;
        }
        if (!this.state.additionalInfo.trim()) {
            this.setState({
                additionalInfoInvalid: true
            });
            valid = false;
        }

        this.setState({
            showMandatoryFields: !valid
        });

        if (!valid || this.state.emailInvalid || this.state.showAdditionalInfoLimitWarning) {
            return;
        }

        this.setState({
            errorMessage: "",
            sending: true
        });

        const data = {
            first_name: this.state.firstName.trim(),
            last_name: this.state.lastName.trim(),
            email: this.state.email,
            company_name: this.state.companyName,
            country: this.state.country,
            subject: this.state.subject,
            additional_info: this.state.additionalInfo
        };

        let errorOccurred = false;

        const response = await sendUserFeedback(data).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({
                    sending: false
                });
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    sending: false,
                    errorMessage: "Couldn't send message."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        if (response && response.status === 210) {
            this.setState({
                sending: false,
                sentSuccessfully: true,
                errorMessage: ""
            });
        } else {
            this.setState({
                errorMessage: "Couldn't send message."
            });
        }
    };

    public render() {
        return <div className="modal is-active">
            <div className="modal-background" style={{
                opacity: 0.5
            }}>&nbsp;</div>
            <div className="modal-content">
                <div
                    style={{
                        backgroundColor: '#ebf1f5',
                        margin: 'auto',
                        padding: '20px 40px',
                        textAlign: 'center',
                        position: 'relative'
                    }}
                >
                    <Button
                        title="Close"
                        className="has-background-light"
                        style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            border: 0
                        }}
                        onClick={() => {
                            this.props.onClose();
                        }}
                    >
                        <Icon className="fas fa-times" style={{
                            color: 'grey',
                            fontSize: '24px',
                            fontWeight: 'normal'
                        }}/>
                    </Button>
                    <div style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        textAlign: 'left'
                    }}>
                        <p>Contact us</p>
                        <p style={{fontSize: '16px'}}>Please reach out via the form below</p>
                    </div>
                    {
                        !this.state.sentSuccessfully ? <div style={{
                            padding: '10px',
                            textAlign: 'left'
                        }}>
                            <div style={{
                                width: '100%'
                            }}>
                                <Field style={{verticalAlign: 'top', marginRight: '5px', display: 'inline-block', minWidth: '265px'}}>
                                    <StyledLabel>
                                        First Name
                                        {this.state.showMandatoryFields ? <MandatoryField>{" *"}</MandatoryField> : <></>}
                                    </StyledLabel>
                                    <Control>
                                        <Input
                                            style={this.state.firstNameInvalid ? INVALID_FIELD_STYLE : undefined}
                                            type="text"
                                            placeholder="First Name"
                                            value={this.state.firstName}
                                            onChange={this.handleFirstNameChange}
                                        />
                                    </Control>
                                    {
                                        this.state.firstNameInvalid ? <InputWarning>Required field</InputWarning> : <></>
                                    }
                                </Field>

                                <Field style={{verticalAlign: 'top', marginRight: '5px', display: 'inline-block', minWidth: '265px'}}>
                                    <StyledLabel>
                                        Last Name
                                        {this.state.showMandatoryFields ? <MandatoryField>{" *"}</MandatoryField> : <></>}
                                    </StyledLabel>
                                    <Control>
                                        <Input
                                            style={this.state.lastNameInvalid ? INVALID_FIELD_STYLE : undefined}
                                            type="text"
                                            placeholder="Last Name"
                                            value={this.state.lastName}
                                            onChange={this.handleLastNameChange}
                                        />
                                    </Control>
                                    {
                                        this.state.lastNameInvalid ? <InputWarning>Required field</InputWarning> : <></>
                                    }
                                </Field>
                            </div>

                            <FieldSpacer/>

                            <div style={{
                                width: '100%'
                            }}>
                                <Field style={{paddingRight: '5px', display: 'inline-block', width: '100%'}}>
                                    <StyledLabel>
                                        Email
                                        {this.state.showMandatoryFields ? <MandatoryField>{" *"}</MandatoryField> : <></>}
                                    </StyledLabel>
                                    <Control>
                                        <Input
                                            style={this.state.emailInvalid ? INVALID_FIELD_STYLE : undefined}
                                            type="text"
                                            placeholder="Email"
                                            value={this.state.email}
                                            onChange={this.handleEmailChange}
                                        />
                                    </Control>
                                    {
                                        this.state.emailInvalid ? <InputWarning>{this.state.emailErrorMessage}</InputWarning> : <></>
                                    }
                                </Field>
                            </div>

                            <FieldSpacer/>

                            <div style={{
                                width: '100%'
                            }}>
                                <Field style={{paddingRight: '5px', display: 'inline-block', width: '100%'}}>
                                    <StyledLabel>
                                        Country
                                        {this.state.showMandatoryFields ? <MandatoryField>{" *"}</MandatoryField> : <></>}
                                    </StyledLabel>
                                    <Control>
                                        <Select
                                            className="is-fullwidth"
                                            style={this.state.countryInvalid ? INVALID_FIELD_STYLE : undefined}
                                            value={this.state.country}
                                            onChange={this.handleCountryChange}
                                        >
                                            {
                                                this.countries.map((country: any) => {
                                                    return <option key={country} value={country}>{country}</option>;
                                                })
                                            }
                                        </Select>
                                    </Control>
                                    {
                                        this.state.countryInvalid ? <InputWarning>Required field</InputWarning> : <></>
                                    }
                                </Field>
                            </div>

                            <FieldSpacer/>

                            <div style={{
                                width: '100%'
                            }}>
                                <Field style={{verticalAlign: 'top', marginRight: '5px', display: 'inline-block', minWidth: '265px'}}>
                                    <StyledLabel>Company Name</StyledLabel>
                                    <Control>
                                        <Input
                                            type="text"
                                            placeholder="Company Name"
                                            value={this.state.companyName}
                                            onChange={this.handleCompanyChange}
                                        />
                                    </Control>
                                </Field>
                                <Field style={{verticalAlign: 'top', marginRight: '5px', display: 'inline-block', minWidth: '265px'}}>
                                    <StyledLabel>
                                        Subject
                                        {this.state.showMandatoryFields ? <MandatoryField>{" *"}</MandatoryField> : <></>}
                                    </StyledLabel>
                                    <Control>
                                        <Select
                                            className="is-fullwidth"
                                            style={this.state.subjectInvalid ? INVALID_FIELD_STYLE : undefined}
                                            value={this.state.subject}
                                            onChange={this.handleSubjectChange}
                                        >
                                            {
                                                this.subjects.map((subject: any) => {
                                                    return <option key={subject} value={subject}>{subject}</option>;
                                                })
                                            }
                                        </Select>
                                    </Control>

                                    {
                                        this.state.subjectInvalid ? <InputWarning>Required field</InputWarning> : <></>
                                    }
                                </Field>
                            </div>
                            <Field style={{paddingRight: '5px', display: 'inline-block', width: '100%'}}>
                                <StyledLabel>
                                    Additional Information
                                    {this.state.showMandatoryFields ? <MandatoryField>{" *"}</MandatoryField> : <></>}
                                </StyledLabel>
                                <Control>
                                    <TextArea
                                        rows={6}
                                        className="has-fixed-size"
                                        style={this.state.additionalInfoInvalid ? INVALID_FIELD_STYLE : undefined}
                                        placeholder="Additional Information"
                                        value={this.state.additionalInfo}
                                        onChange={this.handleAdditionalInfoChange}
                                    />
                                </Control>
                                {
                                    this.state.additionalInfoInvalid ? <InputWarning>Required field</InputWarning> : <></>
                                }
                                {
                                    this.state.showAdditionalInfoLimitWarning ? <InputWarning>
                                        Input size limit has been exceeded. Please decrease the size.
                                    </InputWarning> : <></>
                                }
                            </Field>
                        </div> : <></>
                    }
                    <Spacer/>
                    {
                        this.state.sending ? <div style={{textAlign: 'center'}}>
                            <span>Sending...</span>
                            <Icon className="fas fa-spinner fa-spin"/>
                        </div> : <></>
                    }
                    {
                        !this.state.sending && this.state.sentSuccessfully ? <div style={{textAlign: 'center'}}>
                            <span>Your message was received.</span>
                            <Icon style={{color: 'green', marginLeft: '5px'}} className="fas fa-check"/>
                            <br/>
                            <span>Thank you for contacting us!</span>
                            <Spacer/>
                            <Button onClick={this.closeForm}>Close</Button>
                        </div> : <></>
                    }
                    {
                        !this.state.sending && this.state.errorMessage ? <div style={{textAlign: 'center'}}>
                            <ErrorMessage>{this.state.errorMessage}</ErrorMessage>
                        </div> : <></>
                    }
                    {
                        !this.state.sending && !this.state.sentSuccessfully ? <Button onClick={this.submitForm}>Submit</Button> : <></>
                    }
                    <Spacer/>
                </div>
            </div>
        </div>;
    }
}

export default ContactUsForm;


// WEBPACK FOOTER //
// ./src/components/Contact/index.tsx